import { getSupabaseBrowserClient } from '../lib/supabase'

type RpcError = { message: string }
type RpcResponse = PromiseLike<{ data: unknown; error: RpcError | null }>
type RpcInvoker = (functionName: string, args?: Record<string, unknown>) => RpcResponse

export type OrganisationAuditCategory = 'governance' | 'access'
export type OrganisationAuditEvent = {
  id: string
  occurredAt: string
  eventType: string
  actorName: string
  actorType: 'user' | 'platform_user' | 'system'
  category: OrganisationAuditCategory
}

type OrganisationAuditCursor = {
  occurredAt: string
  id: string
}

function object(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {}
}

function text(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

function parseEvent(value: unknown): OrganisationAuditEvent | null {
  const row = object(value)
  const id = text(row.id)
  const occurredAt = text(row.occurredAt)
  const eventType = text(row.eventType)
  const actorName = text(row.actorName)
  const actorType = text(row.actorType)
  const category = text(row.category)

  if (!id || !occurredAt || !eventType || !actorName) return null
  if (!['user', 'platform_user', 'system'].includes(actorType)) return null
  if (!['governance', 'access'].includes(category)) return null

  return {
    id,
    occurredAt,
    eventType,
    actorName,
    actorType: actorType as OrganisationAuditEvent['actorType'],
    category: category as OrganisationAuditCategory,
  }
}

function parseCursor(value: unknown): OrganisationAuditCursor | null {
  if (value === null) return null
  const row = object(value)
  const occurredAt = text(row.occurredAt)
  const id = text(row.id)
  return occurredAt && id ? { occurredAt, id } : null
}

export async function loadOrganisationAuditHistory(input: {
  organisationId: string
  category?: OrganisationAuditCategory | null
  from?: string | null
  limit?: number
}): Promise<OrganisationAuditEvent[]> {
  const client = getSupabaseBrowserClient() as unknown as { rpc: RpcInvoker }
  const events: OrganisationAuditEvent[] = []
  const seenCursors = new Set<string>()
  let cursor: OrganisationAuditCursor | null = null

  do {
    const { data, error } = await client.rpc('get_r1_organisation_audit_history', {
      p_organisation_id: input.organisationId,
      p_category: input.category ?? null,
      p_from: input.from ?? null,
      p_limit: input.limit ?? 200,
      p_before_occurred_at: cursor?.occurredAt ?? null,
      p_before_id: cursor?.id ?? null,
    })
    if (error) throw new Error(error.message)

    const payload = object(data)
    const pageEvents = Array.isArray(payload.events) ? payload.events : []
    events.push(...pageEvents.map(parseEvent).filter((event): event is OrganisationAuditEvent => event !== null))

    cursor = parseCursor(payload.nextCursor)
    if (cursor) {
      const cursorKey = `${cursor.occurredAt}:${cursor.id}`
      if (seenCursors.has(cursorKey)) throw new Error('Audit history pagination did not advance')
      seenCursors.add(cursorKey)
    }
  } while (cursor)

  return events
}
